import { apiEndpoints as extensionApiEndpoints } from "@afriannotate/extensions";

export const API_CONFIG = {
  gateway: `${window.APP_SETTINGS.hostname}/api`,
  endpoints: {
    // Endpoints contributed by an installed extension package come first so
    // a platform entry of the same name still wins.
    ...(extensionApiEndpoints ?? {}),
    // Users
    users: "/users",
    updateUser: "PATCH:/users/:pk",
    updateUserAvatar: "POST:/users/:pk/avatar",
    deleteUserAvatar: "DELETE:/users/:pk/avatar",
    annotation: "/annotations/:pk/",
    me: "/current-user/whoami",
    hotkeys: "GET:/current-user/hotkeys/",
    updateHotkeys: "PATCH:/current-user/hotkeys/",
    // Email-change flow. POST {new_email} → server stashes
    // proposed_new_email + sends verify link to the NEW address.
    // The active email doesn't change until the user clicks the
    // link in that mailbox.
    changeEmail: "POST:/auth/change-email/",

    // Organization
    organizations: "/organizations/",
    organization: "/organizations/:pk",
    getOrganization: "/organizations/:pk",
    createOrganization: "POST:/organizations/",
    switchOrganization: "POST:/organizations/switch",
    updateOrganization: "PATCH:/organizations/:pk",
    deleteOrganization: "DELETE:/organizations/:pk/delete",
    orgAvatar: "POST:/organizations/:pk/avatar",
    deleteOrgAvatar: "DELETE:/organizations/:pk/avatar",
    orgStats: "/organizations/:pk/stats",
    orgQuota: "/organizations/:pk/quota",
    orgHealth: "/organizations/:pk/health",
    orgQuotaUpdate: "PATCH:/organizations/:pk/quota",
    orgHF: "/organizations/:pk/hf",
    orgHFUpdate: "PUT:/organizations/:pk/hf",
    orgQuotaRequests: "/organizations/:pk/quota-requests",
    orgQuotaRequestCreate: "POST:/organizations/:pk/quota-requests",
    platformQuotaRequests: "/platform/quota-requests",
    platformQuotaRequestDecide: "POST:/platform/quota-requests/:pk/decide",
    orgNonMembers: "/organizations/:pk/non-members",
    orgAddMembers: "POST:/organizations/:pk/add-members",
    orgInviteByEmail: "POST:/organizations/:pk/invite-email",
    orgTransfer: "POST:/organizations/:pk/transfer",
    orgExport: "/organizations/:pk/export",
    memberships: "/organizations/:pk/memberships",
    updateMembership: "PATCH:/organizations/:pk/memberships/:userPk/",
    deleteMembership: "DELETE:/organizations/:pk/memberships/:userPk/",
    userMemberships: "/organizations/:pk/memberships/:userPk",
    suspendMember: "POST:/organizations/:pk/memberships/:userPk/suspend",

    // Org-level policies (Settings → Organization → Policies) + the
    // per-project effective view the settings pages use for lock states.
    orgPolicies: "/organizations/:pk/policies",
    orgPoliciesUpdate: "PUT:/organizations/:pk/policies",
    // Letting the people who run the platform in, for a set time.
    orgSupportAccess: "/organizations/:pk/support-access",
    orgSupportAccessGrant: "POST:/organizations/:pk/support-access",
    orgSupportAccessEnd: "DELETE:/organizations/:pk/support-access",
    projectEffectivePolicies: "/projects/:pk/effective-policies",

    // Auto-suspend rules — list/create on the collection, update/delete by rule id.
    orgAutoSuspendRules: "/organizations/:pk/auto-suspend-rules",
    orgAutoSuspendRuleCreate: "POST:/organizations/:pk/auto-suspend-rules",
    orgAutoSuspendRuleUpdate: "PUT:/organizations/:pk/auto-suspend-rules/:ruleId",
    orgAutoSuspendRuleDelete: "DELETE:/organizations/:pk/auto-suspend-rules/:ruleId",

    // Platform management (staff only)
    platformStats: "/platform/stats",
    platformHealth: "/platform/health",
    platformUsers: "/platform/users",
    platformUserCreate: "POST:/platform/users/create",
    platformUserBulkCreate: "POST:/platform/users/bulk-create",
    platformUserDetail: "/platform/users/:pk",
    platformUserUpdate: "PATCH:/platform/users/:pk",
    platformUserDelete: "POST:/platform/users/:pk/delete",
    platformUserDeletePreview: "GET:/platform/users/:pk/delete-preview",
    platformUserActivate: "POST:/platform/users/:pk/activate",
    platformUserGrantStaff: "POST:/platform/users/:pk/staff",
    platformUserDataAccess: "POST:/platform/users/:pk/data-access",
    platformUserVerifyEmail: "POST:/platform/users/:pk/verify-email",
    platformUserResendSetup: "POST:/platform/users/:pk/resend-setup-link",
    platformUserOrgs: "/platform/users/:pk/orgs",
    platformUserAddOrg: "POST:/platform/users/:pk/orgs",
    platformUserUpdateOrgRole: "PATCH:/platform/users/:pk/orgs",
    platformUserRemoveOrg: "DELETE:/platform/users/:pk/orgs",
    platformOrgs: "/platform/orgs",
    platformBranding: "/platform/branding",
    platformBrandingUpdate: "PATCH:/platform/branding",
    platformNoticeKinds: "/platform/notice-kinds",
    platformAudit: "/platform/audit",
    platformAuditActions: "/platform/audit/actions",
    platformAuditExport: "/platform/audit/export",
    platformNotificationSettings: "/platform/notification-settings",
    platformNotificationSettingsUpdate: "PATCH:/platform/notification-settings",
    platformNotificationTestEmail: "POST:/platform/notification-settings/test-email",
    platformEmailTemplates: "/platform/email-templates",
    platformEmailTemplateCreate: "POST:/platform/email-templates",
    platformEmailTemplate: "/platform/email-templates/:pk",
    platformEmailTemplateUpdate: "PATCH:/platform/email-templates/:pk",
    platformEmailTemplateDelete: "DELETE:/platform/email-templates/:pk",
    platformEmailTemplateApply: "POST:/platform/email-templates/:pk/apply",
    platformQuotaTiers: "/platform/quota-tiers",
    platformQuotaTiersUpdate: "PUT:/platform/quota-tiers",
    feedbackCreate: "POST:/feedback/",
    feedbackList: "/feedback/",
    feedbackReview: "POST:/feedback/:pk/review",
    // In-app testing programme (test_reports app) — retire via Platform → Testing.
    testReportConfig: "/test-reports/config",
    testReportCreate: "POST:/test-reports/",
    testReportList: "/test-reports/",
    testReportMine: "/test-reports/mine",
    testReportRetry: "POST:/test-reports/:pk/retry",
    testReportSync: "POST:/test-reports/sync",
    testReportSettings: "/test-reports/settings",
    testReportSettingsUpdate: "PATCH:/test-reports/settings",
    testChecklist: "/test-reports/checklist",
    testChecklistCheck: "POST:/test-reports/checklist/check",
    testChecklistProgress: "/test-reports/checklist/progress",
    notifications: "/current-user/notifications",
    notificationsUnreadCount: "/current-user/notifications/unread-count",
    notificationMarkRead: "POST:/current-user/notifications/:pk/read",
    notificationsMarkAllRead: "POST:/current-user/notifications/read-all",

    inviteLink: "/invite",
    resetInviteLink: "POST:/invite/reset-token",

    // Project
    projects: "/projects",
    project: "/projects/:pk",
    updateProject: "PATCH:/projects/:pk",
    createProject: "POST:/projects",
    deleteProject: "DELETE:/projects/:pk",
    projectResetCache: "POST:/projects/:pk/summary/reset",

    // Project members (role-based access)
    projectMembers: "/projects/:pk/members/",
    projectAttention: "/projects/:pk/attention",
    projectAddMember: "POST:/projects/:pk/members/",
    projectUpdateMember: "PATCH:/projects/:pk/members/:userPk/",
    projectRemoveMember: "DELETE:/projects/:pk/members/:userPk/",
    projectNonMembers: "/projects/:pk/non-members/",
    projectMyPermissions: "/projects/:pk/my-permissions/",

    // Crowdsource — Discover feed + non-org join/leave.
    // See native/CROWDSOURCE_ARCHITECTURE.md for the design rationale.
    discover: "/discover/",
    discoverMine: "/discover/mine/",
    projectJoin: "POST:/projects/:pk/join/",
    projectLeave: "POST:/projects/:pk/leave/",
    // Owner-side contributor roster (slice 6) — manager-gated.
    createTranslationProject: "POST:/projects/localization/create/",
    // Language requests: volunteers ask for a language, staff fulfil/decline.
    languageRequests: "/projects/localization/requests/",
    createLanguageRequest: "POST:/projects/localization/requests/",
    languageRequestAction: "POST:/projects/localization/requests/:pk/:action/",
    // Publish a project's reviewed translations into a live locale (staff).
    publishLocale: "POST:/projects/localization/:pk/publish/",
    projectLogo: "POST:/projects/:pk/logo/",
    deleteProjectLogo: "DELETE:/projects/:pk/logo/",
    projectReviewAcceptAll: "POST:/projects/:pk/review/accept-all/",
    // GET on the same route: {pending, escalated} counts for the confirm dialog.
    projectReviewAcceptAllPreview: "/projects/:pk/review/accept-all/",
    projectContributors: "/projects/:pk/contributors/",
    projectContributorApprove: "POST:/projects/:pk/contributors/:userPk/",
    projectContributorRemove: "DELETE:/projects/:pk/contributors/:userPk/",

    // Hugging Face Hub bidirectional connector — preview a dataset,
    // import selected columns as tasks, push annotations back to the
    // source or a fork, read the current source binding.
    hfPreview: "POST:/projects/:pk/hub/preview/",
    hfImport: "POST:/projects/:pk/hub/import/",
    hfPush: "POST:/projects/:pk/hub/push/",
    hfSource: "/projects/:pk/hub/source/",
    hfAccess: "/projects/:pk/hub/access/",
    hfSourceClear: "DELETE:/projects/:pk/hub/source/",
    hfSetToken: "POST:/current-user/hf-token/",
    hfClearToken: "DELETE:/current-user/hf-token/",

    // Knowledge Bases (org-scoped) + KB entries.
    knowledgeBases: "/kb/",
    knowledgeBaseCreate: "POST:/kb/",
    knowledgeBase: "/kb/:pk/",
    knowledgeBaseUpdate: "PATCH:/kb/:pk/",
    knowledgeBaseDelete: "DELETE:/kb/:pk/",
    kbEntries: "/kb/:kb_pk/entries/",
    kbEntryCreate: "POST:/kb/:kb_pk/entries/",
    kbEntryUpdate: "PATCH:/kb/:kb_pk/entries/:pk/",
    kbEntryDelete: "DELETE:/kb/:kb_pk/entries/:pk/",
    kbEntriesBulk: "POST:/kb/:kb_pk/entries/bulk",
    kbEntrySearch: "/kb/:kb_pk/search/",

    // Typed feature layers (project-scoped) + nested typed features.
    typedLayers: "/projects/:pk/typed-layers/",
    typedLayerCreate: "POST:/projects/:pk/typed-layers/",
    typedLayer: "/projects/:pk/typed-layers/:layer_pk/",
    typedLayerUpdate: "PATCH:/projects/:pk/typed-layers/:layer_pk/",
    typedLayerDelete: "DELETE:/projects/:pk/typed-layers/:layer_pk/",
    typedFeatures: "/projects/:pk/typed-layers/:layer_pk/features/",
    typedFeatureCreate: "POST:/projects/:pk/typed-layers/:layer_pk/features/",
    typedFeatureUpdate: "PATCH:/projects/:pk/typed-layers/:layer_pk/features/:feature_pk/",
    typedFeatureDelete: "DELETE:/projects/:pk/typed-layers/:layer_pk/features/:feature_pk/",

    // Presigning
    presignUrlForTask: "/../tasks/:taskID/presign",
    presignUrlForProject: "/../projects/:projectId/presign",

    // Config and Import
    configTemplates: "/templates",

    // User-authored project templates (save-as / instantiate)
    projectTemplates: "/project-templates/",
    projectTemplateCreate: "POST:/project-templates/",
    projectTemplate: "/project-templates/:pk/",
    projectTemplateUpdate: "PATCH:/project-templates/:pk/",
    projectTemplateDelete: "DELETE:/project-templates/:pk/",
    projectTemplateInstantiate: "POST:/project-templates/:pk/instantiate/",
    projectSaveAsTemplate: "POST:/projects/:pk/save-as-template/",

    // Workspaces (org-scoped project folders)
    workspaces: "/workspaces/",
    workspaceCreate: "POST:/workspaces/",
    workspace: "/workspaces/:pk/",
    workspaceUpdate: "PATCH:/workspaces/:pk/",
    workspaceDelete: "DELETE:/workspaces/:pk/",
    projectSetWorkspace: "POST:/projects/:pk/set-workspace/",

    // Metrics dashboards
    projectMetricsOverview: "/projects/:pk/metrics/overview/",
    projectMetricsTimeseries: "/projects/:pk/metrics/timeseries/",
    projectMetricsAnnotators: "/projects/:pk/metrics/annotators/",
    projectMetricsAgreement: "/projects/:pk/metrics/agreement/",
    projectMetricsInsights: "/projects/:pk/metrics/insights/",
    projectMetricsReadiness: "/projects/:pk/metrics/readiness/",
    projectMetricsDelivery: "/projects/:pk/metrics/delivery/",

    // Quality settings (metric registry, label-config schema, recompute
    // trigger, in-page Test run for the custom code editor).
    projectQualityMetrics: "/projects/:pk/quality/metrics",
    projectQualitySchema: "/projects/:pk/quality/schema",
    projectQualityRecompute: "POST:/projects/:pk/quality/recompute",
    projectQualityTest: "POST:/projects/:pk/quality/test",
    projectQualityEmbeddingHealth: "POST:/projects/:pk/quality/embedding-health",

    // Translation workbench (TM + glossary).
    projectTranslationSuggest: "/projects/:pk/translation/suggest",
    projectTranslationMemory: "/projects/:pk/translation/memory",
    projectTranslationMemoryAdd: "POST:/projects/:pk/translation/memory",
    projectTranslationMemoryDelete: "DELETE:/projects/:pk/translation/memory/:entry_id",
    projectTranslationTmxImport: "POST:/projects/:pk/translation/memory/import-tmx",
    projectTranslationBackfill: "POST:/projects/:pk/translation/memory/backfill",
    projectGlossary: "/projects/:pk/translation/glossary",
    projectGlossaryAdd: "POST:/projects/:pk/translation/glossary",
    projectGlossaryDelete: "DELETE:/projects/:pk/translation/glossary/:term_id",
    projectGlossaryExtract: "POST:/projects/:pk/translation/glossary/extract",

    // Review queue (automated quality-flag triage)
    projectReviewQueue: "/projects/:pk/review-queue",
    projectReviewQueueAction: "PATCH:/projects/:pk/review-queue/:itemId",
    projectFlagTask: "POST:/projects/:pk/tasks/:taskId/flag",
    orgReviewQueue: "/organizations/:pk/review-queue",

    // Payout runs — org-scoped payment batches over the contribution ledger.
    orgPayouts: "/organizations/:pk/payouts",
    orgPayoutPreview: "/organizations/:pk/payouts/preview",
    orgPayoutCreate: "POST:/organizations/:pk/payouts",
    orgPayoutDetail: "/organizations/:pk/payouts/:runId",
    orgPayoutMarkPaid: "POST:/organizations/:pk/payouts/:runId/mark-paid",
    orgPayoutDelete: "DELETE:/organizations/:pk/payouts/:runId",
    orgReceiptTemplates: "/organizations/:pk/payouts/templates",
    orgReceiptTemplateCreate: "POST:/organizations/:pk/payouts/templates",
    orgReceiptTemplateSetDefault: "PATCH:/organizations/:pk/payouts/templates",
    orgReceiptTemplateDelete: "DELETE:/organizations/:pk/payouts/templates/:templateId",
    orgReceiptTemplateValidate: "POST:/organizations/:pk/payouts/templates/validate",
    orgReceiptTemplatePreview: "POST:/organizations/:pk/payouts/templates/preview",

    // Reviewer workflow (human accept / fix / reject)
    projectAssignReviewers: "POST:/projects/:pk/reviewers/assign",
    projectReviewStreamNext: "/projects/:pk/review-stream/next",
    projectReviewStreamReviewed: "/projects/:pk/review-stream/reviewed",
    // Bulk mode — rapid-fire labelling view (docs/annotation/bulk-mode).
    projectBulkTasks: "/projects/:pk/bulk-tasks",
    submitTaskAnnotation: "POST:/tasks/:taskID/annotations/",
    updateTaskAnnotation: "PATCH:/annotations/:pk/",
    annotationReview: "POST:/annotations/:pk/review",
    annotationDispute: "POST:/annotations/:pk/dispute",
    projectMyRejected: "/projects/:pk/my-rejected",

    orgMetricsOverview: "/organizations/:pk/metrics/overview",
    orgMetricsTimeseries: "/organizations/:pk/metrics/timeseries",
    orgMetricsContributors: "/organizations/:pk/metrics/contributors",
    orgMetricsActivity: "/organizations/:pk/metrics/activity",
    orgMetricsLabelDistribution: "/organizations/:pk/metrics/label-distribution",
    orgMetricsQualityByProject: "/organizations/:pk/metrics/quality-by-project",
    orgActivityFeed: "/organizations/:pk/activity",
    orgEvaluations: "/organizations/:pk/evaluations",

    // Per-user profile (comprehensive work history)
    userProfile: "/users/:pk/profile",
    userTimeseries: "/users/:pk/timeseries",
    userActivity: "/users/:pk/activity",

    validateConfig: "POST:/projects/:pk/validate",
    createSampleTask: "POST:/projects/:pk/sample-task",
    fileUploads: "/projects/:pk/file-uploads",
    deleteFileUploads: "DELETE:/projects/:pk/file-uploads",
    importFiles: "POST:/projects/:pk/import",
    importLimits: "/projects/:pk/import-limits",
    // Audio / video splitter (preprocessing: one recording → many clips).
    mediaSplitProbe: "POST:/projects/:pk/media-split/probe",
    mediaSplitSilences: "POST:/projects/:pk/media-split/silences",
    mediaSplitJobs: "/projects/:pk/media-split",
    mediaSplitCreate: "POST:/projects/:pk/media-split",
    mediaSplitJob: "/projects/:pk/media-split/:job",
    mediaSplitRun: "POST:/projects/:pk/media-split/:job/run",
    mediaSplitDeleteSource: "POST:/projects/:pk/media-split/:job/delete-source",
    uploadInstructionFile: "POST:/projects/:pk/instruction-files",
    reimportFiles: "POST:/projects/:pk/reimport",
    dataSummary: "/projects/:pk/summary",

    // DM
    deleteTabs: "DELETE:/dm/views/reset",

    // Storages
    storageConnections: "/storages/connections",
    listStorages: "/storages/:target?",
    storageTypes: "/storages/:target?/types",
    storageForms: "/storages/:target?/:type/form",
    createStorage: "POST:/storages/:target?/:type",
    deleteStorage: "DELETE:/storages/:target?/:type/:pk",
    updateStorage: "PATCH:/storages/:target?/:type/:pk",
    syncStorage: "POST:/storages/:target?/:type/:pk/sync",
    storageHealth: "POST:/storages/health/:type/:pk",
    storageHealthCors: "POST:/storages/health/:type/:pk/cors",
    validateStorage: "POST:/storages/:target?/:type/validate",
    storageFiles: "POST:/storages/:target?/:type/files",

    // ML
    mlBackends: "GET:/ml",
    mlBackend: "GET:/ml/:pk",
    addMLBackend: "POST:/ml",
    updateMLBackend: "PATCH:/ml/:pk",
    deleteMLBackend: "DELETE:/ml/:pk",
    trainMLBackend: "POST:/ml/:pk/train",
    predictWithML: "POST:/ml/:pk/predict/test",
    projectModelVersions: "/projects/:pk/model-versions",
    deletePredictions: "DELETE:/projects/:pk/model-versions",
    modelVersions: "/ml/:pk/versions",
    mlInteractive: "POST:/ml/:pk/interactive-annotating",

    // Annotator consent
    consentTemplates: "GET:/consent/templates",
    consentTemplate: "GET:/consent/templates/:pk",
    addConsentTemplate: "POST:/consent/templates",
    updateConsentTemplate: "PATCH:/consent/templates/:pk",
    deleteConsentTemplate: "DELETE:/consent/templates/:pk",
    consentRequirements: "GET:/consent/requirements",
    addConsentRequirement: "POST:/consent/requirements",
    deleteConsentRequirement: "DELETE:/consent/requirements/:pk",
    consentSignatures: "GET:/consent/signatures",
    addConsentSignature: "POST:/consent/signatures",
    consentPending: "GET:/consent/projects/:project_id/pending/",
    consentVerifyChain: "GET:/consent/organizations/:org_id/verify-chain/",
    consentStarters: "GET:/consent/starters/",
    consentWithdraw: "POST:/consent/withdraw/",
    // PDF download is fetched directly via window.open / a tag —
    // not via callApi — because we want a real browser download
    // dialog, not a JSON fetch. Endpoint is referenced as a string
    // so we don't have to special-case binary responses in the API
    // wrapper. URL: /api/consent/signatures/<id>/pdf/

    // Export
    export: "/projects/:pk/export",
    previousExports: "/projects/:pk/export/files",
    exportFormats: "/projects/:pk/export/formats",
    exportPreview: "/projects/:pk/export/preview",
    exportLens: "POST:/projects/:pk/export/lens",
    // Annotations + recorded media (Audio/VideoRecord clips + pose
    // sidecars) as one ZIP; fetched via exportMediaBundleRaw for the blob.
    exportMediaBundle: "/projects/:pk/export/media-bundle",

    // Version
    version: "/version",

    // Webhook
    webhooks: "/webhooks",
    webhook: "/webhooks/:pk",
    updateWebhook: "PATCH:/webhooks/:pk",
    createWebhook: "POST:/webhooks",
    deleteWebhook: "DELETE:/webhooks/:pk",
    webhooksInfo: "/webhooks/info",
    webhookDeliveries: "/webhooks/:pk/deliveries",

    // Tokens
    accessTokenList: "GET:/token",
    accessTokenGetRefreshToken: "POST:/token",
    accessTokenRevoke: "POST:/token/blacklist",

    accessTokenSettings: "GET:/jwt/settings",
    accessTokenUpdateSettings: "POST:/jwt/settings",

    // FSM
    fsmStateHistory: "GET:/fsm/entities/:entityType/:entityId/history",
  },
  alwaysExpectJSON: false,
};
